import { useMemo, useState } from "react";
import { CalendarRange, Eye, EyeOff } from "lucide-react";

/* ============================================================================
 * Read-only periodisation timeline (visual only).
 * Reads the already-loaded plan tree (plan.nodes) and the date / load fields
 * that already exist. No API calls, no backend, no new data models.
 * Volume %/Intensity % are derived in-browser from the SAME inputs the backend
 * load summary uses: Σ(planned_minutes × target_rpe) for sessions, and mean
 * target_rpe (0–10) → %. Nothing here is persisted or mutated.
 * ========================================================================== */

const BRAND = "#f47a20";

const PHASE_COLORS = {
  general_prep: { fill: "rgba(16,185,129,0.22)", border: "#10b981", text: "#6ee7b7", label: "General Prep" },
  specific_prep: { fill: "rgba(56,189,248,0.22)", border: "#38bdf8", text: "#bae6fd", label: "Specific Prep" },
  pre_competition: { fill: "rgba(167,139,250,0.22)", border: "#a78bfa", text: "#ddd6fe", label: "Pre-Competition" },
  main_competition: { fill: "rgba(251,113,133,0.22)", border: "#fb7185", text: "#fecdd3", label: "Main Competition" },
  transition: { fill: "rgba(148,163,184,0.22)", border: "#94a3b8", text: "#e2e8f0", label: "Transition" },
};
const NEUTRAL = { fill: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.22)", text: "#e4e4e7" };

/* Cool blue-grey ladder: subtle tints so phase colours still stand out. */
const ROW_COLORS = {
  multiannual: { fill: "rgba(100,116,139,0.18)", border: "#64748b", text: "#cbd5e1" },
  annual: { fill: "rgba(99,102,241,0.18)", border: "#6366f1", text: "#c7d2fe" },
  macrocycle: { fill: "rgba(14,165,233,0.18)", border: "#0ea5e9", text: "#bae6fd" },
  mesocycle: { fill: "rgba(234,179,8,0.20)", border: "#eab308", text: "#fde68a" },
  microcycle: { fill: "rgba(217,70,239,0.20)", border: "#d946ef", text: "#f5d0fe" },
};

const ROW_META = {
  multiannual: { label: "Multiannual", h: 34 },
  annual: { label: "Annual", h: 34 },
  macrocycle: { label: "Macrocycle", h: 34 },
  period: { label: "Phase", h: 34 },
  mesocycle: { label: "Mesocycle", h: 34 },
  microcycle: { label: "Microcycle", h: 30 },
};

/* ---- date helpers (UTC, self-contained) ---- */
const toUTC = (iso) => new Date(iso + "T00:00:00Z");
const durationDays = (a, b) => (a && b ? Math.round((toUTC(b) - toUTC(a)) / 86400000) + 1 : 0);
const addDaysISO = (iso, n) => {
  const d = toUTC(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const fmtDate = (s) => (s ? toUTC(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "—");

export default function PeriodisationTimeline({ plan, level, selectedByType = {}, onSelectNode }) {
  const nodes = plan?.nodes || [];
  const [showLegend, setShowLegend] = useState(true);

  const model = useMemo(() => {
    const byType = (t) => nodes.filter((n) => n.type === t);
    const childrenIndex = {};
    nodes.forEach((n) => {
      if (!childrenIndex[n.parent_id]) childrenIndex[n.parent_id] = [];
      childrenIndex[n.parent_id].push(n);
    });
    const descendants = (id) => {
      const out = [];
      const stack = [...(childrenIndex[id] || [])];
      while (stack.length) {
        const c = stack.pop();
        out.push(c);
        if (childrenIndex[c.id]) stack.push(...childrenIndex[c.id]);
      }
      return out;
    };
    // Load derived from descendant sessions (same inputs as backend load calc).
    const loadOf = (node) => {
      const sessions = descendants(node.id).filter((n) => n.type === "session");
      let au = 0;
      const rpes = [];
      sessions.forEach((s) => {
        const mins = Number(s.planned_minutes);
        const rpe = Number(s.target_rpe);
        if (mins && rpe) au += mins * rpe;
        if (rpe) rpes.push(rpe);
      });
      const meanRpe = rpes.length ? rpes.reduce((a, b) => a + b, 0) / rpes.length : null;
      return { au: Math.round(au), meanRpe, sessions: sessions.length };
    };

    const dated = nodes.filter((n) => n.start_date && n.end_date);
    const spanStart = dated.length ? dated.map((n) => n.start_date).sort()[0] : null;
    const spanEnd = dated.length ? dated.map((n) => n.end_date).sort().slice(-1)[0] : null;
    const totalDays = durationDays(spanStart, spanEnd);

    const sorted = (t) => byType(t).filter((n) => n.start_date && n.end_date)
      .sort((a, b) => (a.start_date < b.start_date ? -1 : 1));

    // Pre-compute microcycle load with sequential numbering.
    const micros = sorted("microcycle").map((m, i) => ({ node: m, num: i + 1, ...loadOf(m) }));
    const maxAu = Math.max(1, ...micros.map((m) => m.au));

    return { byType, sorted, loadOf, spanStart, spanEnd, totalDays, micros, maxAu };
  }, [nodes]);

  const { sorted, loadOf, spanStart, spanEnd, totalDays, micros, maxAu } = model;

  if (!nodes.length || !spanStart || !totalDays) {
    return (
      <div className="fl-card p-6 text-center" data-testid="timeline-empty">
        <div className="flex items-center justify-center gap-2 text-sm text-zinc-300">
          <CalendarRange size={16} className="text-[#f47a20]" /> No plan structure to visualise yet.
        </div>
        <div className="text-[11px] text-zinc-500 mt-1">Add macrocycles, mesocycles and microcycles below (or generate a draft) to see the timeline.</div>
      </div>
    );
  }

  const pos = (node) => {
    const offset = durationDays(spanStart, node.start_date) - 1;
    const len = durationDays(node.start_date, node.end_date);
    return { left: Math.max(0, (offset / totalDays) * 100), width: Math.max(0.4, (len / totalDays) * 100) };
  };

  /* ---- month + week header ---- */
  const monthSegs = [];
  {
    let cur = spanStart.slice(0, 8) + "01"; // first of the month of spanStart
    // guard: ensure cur <= spanStart's month start
    let guard = 0;
    while (cur <= spanEnd && guard < 400) {
      const d = toUTC(cur);
      const nextMonthISO = (() => {
        const n = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
        return n.toISOString().slice(0, 10);
      })();
      const segStart = cur < spanStart ? spanStart : cur;
      const segEnd = addDaysISO(nextMonthISO, -1) > spanEnd ? spanEnd : addDaysISO(nextMonthISO, -1);
      const offset = durationDays(spanStart, segStart) - 1;
      const len = durationDays(segStart, segEnd);
      monthSegs.push({
        key: cur,
        left: (offset / totalDays) * 100,
        width: (len / totalDays) * 100,
        label: d.toLocaleDateString("en-GB", { month: "short", timeZone: "UTC" }),
        year: d.toLocaleDateString("en-GB", { year: "2-digit", timeZone: "UTC" }),
      });
      cur = nextMonthISO;
      guard++;
    }
  }
  const weekTicks = [];
  for (let i = 7; i < totalDays; i += 7) weekTicks.push((i / totalDays) * 100);

  const trackWidth = monthSegs.length > 12 ? `${monthSegs.length * 120}px` : "100%";

  const isSelected = (node) => selectedByType[node.type] === node.id;

  /* ---- a row of positioned blocks ---- */
  const BlockRow = ({ type, list, colorFn, labelFn }) => (
    <div className="flex items-stretch gap-2" data-testid={`timeline-row-${type}`}>
      <div className="w-24 shrink-0 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center">{ROW_META[type].label}</div>
      <div className="relative flex-1" style={{ height: ROW_META[type].h }}>
        {list.map((n) => {
          const { left, width } = pos(n);
          const c = colorFn(n);
          const sel = isSelected(n);
          return (
            <button
              key={n.id}
              onClick={() => onSelectNode?.(n)}
              title={`${labelFn(n)} · ${fmtDate(n.start_date)} – ${fmtDate(n.end_date)}`}
              data-testid={`timeline-block-${n.id}`}
              className="absolute top-0 bottom-0 rounded-md overflow-hidden text-left px-1.5 flex items-center transition-transform hover:-translate-y-[1px]"
              style={{
                left: `${left}%`, width: `${width}%`, minWidth: 10,
                background: c.fill,
                border: sel ? `2px solid ${c.border}` : `1px solid ${c.border}`,
                boxShadow: sel ? `0 0 0 2px rgba(244,122,32,0.35)` : "none",
                color: c.text,
                zIndex: sel ? 5 : 1,
              }}
            >
              <span className="block w-full text-[10px] font-semibold" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{labelFn(n)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ---- a row of vertical bars (one per microcycle) ---- */
  const BarRow = ({ kind, data, max }) => (
    <div className="flex items-stretch gap-2" data-testid={`timeline-${kind}-row`}>
      <div className="w-24 shrink-0 text-[10px] uppercase tracking-widest text-zinc-500 flex items-center">
        {kind === "volume" ? "Volume %" : "Intensity %"}
      </div>
      <div className="relative flex-1 border-t border-white/5" style={{ height: 56 }}>
        {data.map((m) => {
          const { left, width } = pos(m.node);
          const pct = kind === "volume"
            ? (m.au ? Math.round((m.au / max) * 100) : 0)
            : (m.meanRpe ? Math.round(m.meanRpe * 10) : 0);
          const color = kind === "volume" ? "#10b981" : BRAND;
          return (
            <button
              key={m.node.id}
              onClick={() => onSelectNode?.(m.node)}
              title={kind === "volume"
                ? `Week ${m.num} · ${pct}% volume · ${m.au} AU · ${m.sessions} session${m.sessions === 1 ? "" : "s"}${m.meanRpe ? ` · mean RPE ${m.meanRpe.toFixed(1)}` : ""}`
                : `Week ${m.num} · ${pct}% intensity · mean RPE ${m.meanRpe ? m.meanRpe.toFixed(1) : "—"} · ${m.sessions} session${m.sessions === 1 ? "" : "s"} · ${m.au} AU`}
              data-testid={`timeline-${kind}-bar-${m.node.id}`}
              className="absolute bottom-0 flex flex-col justify-end items-center group"
              style={{ left: `${left}%`, width: `${width}%`, minWidth: 6, height: "100%" }}
            >
              <span className="text-[8px] text-zinc-500 opacity-0 group-hover:opacity-100 transition-opacity leading-none mb-0.5">{pct}</span>
              <div className="w-[70%] rounded-t-sm transition-all" style={{ height: `${Math.max(2, pct * 0.42)}px`, background: pct ? color : "rgba(255,255,255,0.08)", opacity: pct ? 0.85 : 1 }} />
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ---- which rows to render for the current selector level ---- */
  const macroColor = () => ROW_COLORS.macrocycle;
  const periodColor = (n) => PHASE_COLORS[n.phase_type] || NEUTRAL;
  const rowColor = (t) => () => ROW_COLORS[t] || NEUTRAL;
  const lbl = (n) => n.display_name || n.name || "—";
  const periodLbl = (n) => (PHASE_COLORS[n.phase_type]?.label) || n.display_name || n.name || "Phase";
  const microLbl = (n) => {
    const m = micros.find((x) => x.node.id === n.id);
    return m ? `${m.num}` : (n.display_name || n.name || "—");
  };

  const stacked = level === "all";
  const showRow = (t) => stacked || level === t;
  const showBars = stacked || ["macrocycle", "mesocycle", "microcycle"].includes(level);

  // In a standalone level the bars reflect that level's own granularity.
  const barData = (stacked || level === "microcycle")
    ? micros
    : (level === "macrocycle" || level === "mesocycle")
      ? sorted(level).map((n, i) => ({ node: n, num: i + 1, ...loadOf(n) }))
      : [];
  const barMax = Math.max(1, ...barData.map((m) => m.au));
  const effMax = (stacked || level === "microcycle") ? maxAu : barMax;

  return (
    <div className="fl-card fl-card-elevated p-4 sm:p-5" data-testid="periodisation-timeline">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2 text-[#f47a20] text-sm font-semibold">
          <CalendarRange size={15} /> Timeline
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-normal">read-only</span>
        </div>
        <div className="text-[11px] text-zinc-500 flex items-center gap-3">
          <span>{fmtDate(spanStart)} – {fmtDate(spanEnd)}</span>
          {stacked && sorted("period").length > 0 && (
            <button
              onClick={() => setShowLegend((v) => !v)}
              data-testid="timeline-legend-toggle"
              className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#f47a20] transition-colors"
            >
              {showLegend ? <EyeOff size={12} /> : <Eye size={12} />}
              {showLegend ? "Hide legend" : "Show legend"}
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto no-scrollbar">
        <div style={{ width: trackWidth, minWidth: "100%" }}>
          {/* month + week header */}
          <div className="flex items-stretch gap-2 mb-1">
            <div className="w-24 shrink-0" />
            <div className="relative flex-1 h-8 border-b border-white/10">
              {monthSegs.map((s) => (
                <div key={s.key} className="absolute top-0 bottom-0 border-l border-white/10 flex items-center" style={{ left: `${s.left}%`, width: `${s.width}%` }}>
                  <span className="text-[10px] text-zinc-400 pl-1 whitespace-nowrap">{s.label} <span className="text-zinc-600">’{s.year}</span></span>
                </div>
              ))}
              {weekTicks.map((t, i) => (
                <div key={i} className="absolute top-5 bottom-0 border-l border-white/5" style={{ left: `${t}%` }} />
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            {showRow("multiannual") && sorted("multiannual").length > 0 && (
              <BlockRow type="multiannual" list={sorted("multiannual")} colorFn={rowColor("multiannual")} labelFn={lbl} />
            )}
            {showRow("annual") && sorted("annual").length > 0 && (
              <BlockRow type="annual" list={sorted("annual")} colorFn={rowColor("annual")} labelFn={lbl} />
            )}
            {showRow("macrocycle") && sorted("macrocycle").length > 0 && (
              <BlockRow type="macrocycle" list={sorted("macrocycle")} colorFn={macroColor} labelFn={lbl} />
            )}
            {(stacked) && sorted("period").length > 0 && (
              <BlockRow type="period" list={sorted("period")} colorFn={periodColor} labelFn={periodLbl} />
            )}
            {showRow("mesocycle") && sorted("mesocycle").length > 0 && (
              <BlockRow type="mesocycle" list={sorted("mesocycle")} colorFn={rowColor("mesocycle")} labelFn={lbl} />
            )}
            {showRow("microcycle") && micros.length > 0 && (
              <BlockRow type="microcycle" list={micros.map((m) => m.node)} colorFn={rowColor("microcycle")} labelFn={microLbl} />
            )}

            {showBars && barData.length > 0 && (
              <div className="pt-2 mt-1 border-t border-white/5 space-y-1.5">
                <BarRow kind="volume" data={barData} max={effMax} />
                <BarRow kind="intensity" data={barData} max={effMax} />
              </div>
            )}
          </div>
        </div>
      </div>

      {stacked && showLegend && sorted("period").length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pt-3 border-t border-white/5" data-testid="timeline-phase-legend">
          {Object.values(PHASE_COLORS).map((c) => (
            <div key={c.label} className="flex items-center gap-1.5 text-[10px] text-zinc-400">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: c.fill, border: `1px solid ${c.border}` }} />
              {c.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
